import React, { useEffect, useState } from "react";
import { ICreateDocumentModalProps } from "./definitions";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import styles from "./style.module.scss";
import {
  FormField,
  Box,
  SpaceBetween,
  Button,
  ColumnLayout,
  Form,
  Input,
  Spinner,
} from "@amzn/awsui-components-react";
import {
  ControlledInput,
  ControlledSelect,
} from "@amzn/react-hook-form-polaris";
import { zodResolver } from "@hookform/resolvers/zod";
import { createDocumentSchema } from "models/schemas";
import { get, useForm, FormProvider } from "react-hook-form";
import UploadFile from "components/upload-file/UploadFile";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";

const CreateDocumentModal: React.FC<ICreateDocumentModalProps> = ({
  onCreate,
  visible,
  setVisible,
  isFileUpload,
  title,
  defaultDocumentType,
  gaiaObjectType,
}) => {
  const [resetForm, setResetForm] = React.useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState(false);
  const [link, setLink] = React.useState("");
  const [linkError, setLinkError] = useState(false);
  const [fileName, setFileName] = React.useState("");
  const [fileNameError, setFileNameError] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const uiContent: any = useGaiaObjectUiContent(gaiaObjectType);

  const formMethods = useForm({
    mode: "onBlur",
    resolver: zodResolver(createDocumentSchema),
    defaultValues: {
      documentType: defaultDocumentType ? defaultDocumentType : undefined,
    },
  });

  useEffect(() => {
    if (resetForm) {
      formMethods.reset();
    }
  }, [resetForm]);

  const onValid = (data: any, _e: any) => {
    if (isFileUpload) {
      files[0] ? createDoc(data) : setFileError(true);
    } else {
      if (link && fileName) {
        createDoc(data);
      } else {
        if (!link) setLinkError(true);
        if (!fileName) setFileNameError(true);
      }
    }
  };

  const oninvalid = (_data: any, _e: any) => {
    if (!files.length) setFileError(true);
    if (!link) setLinkError(true);
    if (!fileName) setFileNameError(true);
  };

  const onDismiss = (e: any) => {
    if (e.detail.reason === "overlay") return;
    resetModal();
  };
  const onCancelSave = () => resetModal();

  const createDoc = (data: any) => {
    setIsDisabled(true);
    onCreate(data, files[0], link, fileName, () => {
      resetModal();
    });
  };

  const resetModal = () => {
    setVisible(false);
    formMethods.reset({
      documentType: defaultDocumentType ? defaultDocumentType : undefined,
    });
    setFiles([]);
    setLink("");
    setFileName("");
    setFileError(false);
    setLinkError(false);
    setFileNameError(false);
    setIsDisabled(false);
    setResetForm(resetForm + 1);
  };

  return (
    <Modal
      onDismiss={onDismiss}
      visible={visible}
      header={title}
      data-testid="file-upload-header"
    >
      Gaia keeps files for 10 years.
      <br />
      <br />
      <form onSubmit={formMethods.handleSubmit(onValid, oninvalid)}>
        <FormProvider {...formMethods}>
          <Form>
            <ColumnLayout columns={1}>
              {isFileUpload ? (
                <FormField
                  label="Upload file"
                  data-testid="file-upload-form"
                  errorText={fileError ? "Please select one file." : undefined}
                >
                  <div>
                    <UploadFile
                      setFiles={setFiles}
                      setFileName={setFileName}
                      setFileError={setFileError}
                      files={files}
                    />
                  </div>
                </FormField>
              ) : (
                <FormField
                  label="Link"
                  errorText={linkError ? "Required" : undefined}
                  description="Provide the url to the file."
                >
                  <Input
                    value={link}
                    onChange={({ detail }) => {
                      setLinkError(!detail.value || detail.value === "");
                      setLink(detail.value);
                    }}
                  />
                </FormField>
              )}
              <FormField
                label={<span>Name {isFileUpload && <i>- optional</i>}</span>}
                errorText={
                  !isFileUpload && fileNameError
                    ? "Required"
                    : get(formMethods.formState?.errors, "fileName.message")
                }
                description={
                  isFileUpload
                    ? "Default name is the uploaded file name."
                    : undefined
                }
              >
                <ControlledInput
                  name="fileName"
                  onChange={({ detail }) => {
                    if (!isFileUpload)
                      setFileNameError(!detail.value || detail.value === "");
                    setFileName(detail.value);
                  }}
                  placeholder={fileName}
                />
              </FormField>
              <FormField
                label="File type"
                errorText={get(
                  formMethods.formState?.errors,
                  "documentType.message",
                )}
              >
                <ControlledSelect
                  name="documentType"
                  readOnly={!!defaultDocumentType}
                  options={uiContent.documentTypeOptions}
                  control={formMethods.control}
                  filteringType="auto"
                />
              </FormField>
            </ColumnLayout>
          </Form>
          <br />
          <div className={styles.modalFooter}>
            <Box>
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  formAction="none"
                  onClick={onCancelSave}
                  variant="link"
                  disabled={isDisabled}
                >
                  Cancel
                </Button>
                <Button variant="primary" disabled={isDisabled}>
                  {isDisabled ? <Spinner /> : null}
                  {isFileUpload ? "Upload" : "Add"}
                </Button>
              </SpaceBetween>
            </Box>
          </div>
        </FormProvider>
      </form>
    </Modal>
  );
};

export default CreateDocumentModal;
